import React from 'react';
import { Box, Paper, Typography, useTheme, alpha, Tooltip, useMediaQuery } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface StatItem {
  icon: React.ReactNode;
  value: number | string;
  label: string;
  color: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
  onClick?: () => void;
  tooltip?: string;
}

interface CompactStatsBarProps {
  stats: StatItem[];
  sx?: SxProps<Theme>;
}

export const CompactStatsBar: React.FC<CompactStatsBarProps> = ({ stats, sx }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2, md: 2.5 },
        background: alpha(theme.palette.background.paper, 0.9),
        backdropFilter: 'blur(12px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        borderRadius: 3,
        ...sx
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 1.5 },
          alignItems: 'stretch',
        }}
      >
        {stats.map((stat, index) => {
          const isActionable = stat.color === 'error' || stat.color === 'warning';
          const hasValue = typeof stat.value === 'number' ? stat.value > 0 : !!stat.value;

          return (
            <Tooltip key={index} title={stat.tooltip || stat.label} arrow placement="top">
              <Box
                onClick={stat.onClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.25 },
                  cursor: stat.onClick ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  borderRadius: 2,
                  px: { xs: 1.25, sm: 1.75, md: 2 },
                  py: { xs: 1, sm: 1.25 },
                  minWidth: { xs: 'calc(50% - 8px)', sm: 'auto' },
                  flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 0%' },
                  backgroundColor: isActionable && hasValue
                    ? alpha(theme.palette[stat.color].main, 0.04)
                    : 'transparent',
                  borderLeft: isActionable && hasValue
                    ? `3px solid ${alpha(theme.palette[stat.color].main, 0.5)}`
                    : '3px solid transparent',
                  '&:hover': stat.onClick ? {
                    backgroundColor: alpha(theme.palette[stat.color].main, 0.08),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette[stat.color].main, 0.1)}`,
                    '& .stat-icon': {
                      transform: 'scale(1.1)',
                    },
                    '& .stat-value': {
                      color: theme.palette[stat.color].main,
                    }
                  } : {}
                }}
              >
                <Box
                  className="stat-icon"
                  sx={{
                    width: { xs: 34, sm: 38, md: 40 },
                    height: { xs: 34, sm: 38, md: 40 },
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette[stat.color].main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s ease',
                    flexShrink: 0,
                    '& > svg': {
                      fontSize: { xs: 18, sm: 20, md: 22 },
                      color: theme.palette[stat.color].main,
                    }
                  }}
                >
                  {stat.icon}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    className="stat-value"
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.35rem' },
                      lineHeight: 1.1,
                      color: isActionable && hasValue
                        ? theme.palette[stat.color].dark
                        : theme.palette.text.primary,
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                      fontWeight: 500,
                      lineHeight: 1.3,
                      display: { xs: isMobile ? 'block' : 'none', sm: 'block' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Paper>
  );
};
